import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { DataPacket_Kind, RemoteParticipant, RoomEvent } from 'livekit-client'

import { useMaybeRoomContext } from '@/src/context'
import { handlePublishData } from '@/src/helpers/handlePublishData'
import { useAppSelector } from '@/src/store/hooks'
import { selectPrompt, selectQuestionSections } from '@/src/store/slices/meetData'
import {
  applyQuestionCatalogState,
  buildPanelSkills,
  buildSectionsFromCatalog,
  parseQuestionCatalogRequest,
  parseQuestionCatalogState,
  resolveAskedQuestionId,
  type PanelQuestion,
  type PanelSkill,
} from './textAgentQuestionCatalog'

type QuestionLocation = {
  skillIndex: number
  skillId: string
  questionId: string
}

type DeleteGeneratedQuestionResult = {
  skills: PanelSkill[]
  deletedLocation: QuestionLocation | null
}

interface TextAgentQuestionsPanelProps {
  isOpen: boolean
  onClose: () => void
}

type GeneratedQuestionMessage = {
  requestId: string
  skillId: string
  skillName: string
  question?: string
  error?: string
}

type SyncQuestionCatalogControlMessage = {
  action: 'sync_question_catalog'
  sessionId?: string
  questions: Array<{
    id: string
    text: string
    skillId: string
    skillName: string
    answered: boolean
    generated?: boolean
  }>
}

const buildQuestionCatalogMessage = (
  skills: PanelSkill[],
  sessionId?: string,
): SyncQuestionCatalogControlMessage => ({
  action: 'sync_question_catalog',
  ...(sessionId ? { sessionId } : {}),
  questions: skills.flatMap((skill) =>
    skill.questions.map((question) => ({
      id: question.id,
      text: question.text,
      skillId: skill.id,
      skillName: skill.name,
      answered: question.answered,
      ...(question.generated ? { generated: true } : {}),
    })),
  ),
})

const GENERAL_SECTION_HEADER = /^общие вопросы$/i
const SKILL_SECTION_HEADER = /^навык:\s*(.+)$/i

const normalizeComparableText = (value: string) =>
  value
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const getTokenOverlap = (a: string, b: string) => {
  const aTokens = a.split(' ').filter((token) => token.length > 2)
  const bTokens = b.split(' ').filter((token) => token.length > 2)

  if (aTokens.length === 0 || bTokens.length === 0) {
    return 0
  }

  const bSet = new Set(bTokens)
  const matched = aTokens.filter((token) => bSet.has(token)).length
  return matched / Math.min(aTokens.length, bTokens.length)
}

const areQuestionsMatching = (source: string, incoming: string) => {
  const normalizedSource = normalizeComparableText(source)
  const normalizedIncoming = normalizeComparableText(incoming)

  if (!normalizedSource || !normalizedIncoming) {
    return false
  }

  if (normalizedSource === normalizedIncoming) {
    return true
  }

  if (
    normalizedSource.length >= 24 &&
    normalizedIncoming.length >= 24 &&
    (normalizedIncoming.includes(normalizedSource) || normalizedSource.includes(normalizedIncoming))
  ) {
    return true
  }

  return getTokenOverlap(normalizedSource, normalizedIncoming) >= 0.8
}

const isLikelyQuestionText = (value: string) => {
  const normalizedValue = normalizeComparableText(value)

  if (!normalizedValue) {
    return false
  }

  if (value.trim().endsWith('?')) {
    return true
  }

  return /^(как|какие|какой|какая|какое|что|почему|зачем|где|когда|сколько|расскажите|опишите|приведите|назовите|уточните|можете ли|были ли|есть ли)(?=\s|$)/i.test(
    normalizedValue,
  )
}

const buildSectionsFromPrompt = (assistantPrompt: string): PanelSkill[] => {
  const promptLines = assistantPrompt
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)

  const sections: Array<{ name: string; questions: string[] }> = []
  let currentSection: { name: string; questions: string[] } | null = null

  const flushCurrentSection = () => {
    if (!currentSection || currentSection.questions.length === 0) {
      currentSection = null
      return
    }

    sections.push(currentSection)
    currentSection = null
  }

  for (const line of promptLines) {
    const skillMatch = line.match(SKILL_SECTION_HEADER)

    if (GENERAL_SECTION_HEADER.test(line) || skillMatch) {
      flushCurrentSection()
      currentSection = {
        name: skillMatch?.[1]?.trim() || 'Общие вопросы',
        questions: [],
      }
      continue
    }

    if (!currentSection) {
      continue
    }

    if (/^[-•]\s+/.test(line)) {
      const question = line.replace(/^[-•]\s+/, '').trim()
      if (question && isLikelyQuestionText(question)) {
        currentSection.questions.push(question)
      }
    }
  }

  flushCurrentSection()

  const fallbackQuestions = sections.length
    ? []
    : promptLines
        .filter((line) => /^[-•]\s+/.test(line))
        .map((line) => line.replace(/^[-•]\s+/, '').trim())
        .filter((line) => line && isLikelyQuestionText(line))

  const normalizedSections = sections.length
    ? sections
    : fallbackQuestions.length
      ? [{ name: 'Вопросы по вакансии', questions: fallbackQuestions }]
      : []

  return buildPanelSkills(normalizedSections)
}

const findQuestionLocation = (
  skills: PanelSkill[],
  predicate: (question: PanelQuestion, skill: PanelSkill) => boolean,
): QuestionLocation | null => {
  for (let skillIndex = 0; skillIndex < skills.length; skillIndex += 1) {
    const skill = skills[skillIndex]
    const question = skill.questions.find((item) => predicate(item, skill))

    if (question) {
      return {
        skillIndex,
        skillId: skill.id,
        questionId: question.id,
      }
    }
  }

  return null
}

const getFirstUnansweredQuestionLocation = (skills: PanelSkill[]) =>
  findQuestionLocation(skills, (question) => !question.answered)

const getNextUnansweredQuestionLocation = (
  skills: PanelSkill[],
  currentLocation: QuestionLocation | null,
  excludeQuestionId?: string,
): QuestionLocation | null => {
  const flatQuestions = skills.flatMap((skill, skillIndex) =>
    skill.questions.map((question) => ({
      skill,
      skillIndex,
      question,
    })),
  )

  if (!flatQuestions.length) {
    return null
  }

  const currentIndex = currentLocation
    ? flatQuestions.findIndex((item) => item.question.id === currentLocation.questionId)
    : -1

  const orderedQuestions = [
    ...flatQuestions.slice(Math.max(currentIndex + 1, 0)),
    ...flatQuestions.slice(0, Math.max(currentIndex + 1, 0)),
  ]

  const nextQuestion = orderedQuestions.find(
    (item) => !item.question.answered && item.question.id !== excludeQuestionId,
  )

  if (!nextQuestion) {
    return null
  }

  return {
    skillIndex: nextQuestion.skillIndex,
    skillId: nextQuestion.skill.id,
    questionId: nextQuestion.question.id,
  }
}

const markQuestionCompleted = (skills: PanelSkill[], questionId: string) =>
  skills.map((skill) => ({
    ...skill,
    questions: skill.questions.map((question) =>
      question.id === questionId ? { ...question, asked: true, answered: true } : question,
    ),
  }))

const toggleQuestionCompleted = (skills: PanelSkill[], questionId: string) =>
  skills.map((skill) => ({
    ...skill,
    questions: skill.questions.map((question) =>
      question.id === questionId
        ? { ...question, asked: !question.answered, answered: !question.answered }
        : question,
    ),
  }))

const deleteGeneratedQuestion = (
  skills: PanelSkill[],
  predicate: (question: PanelQuestion, skill: PanelSkill) => boolean,
): DeleteGeneratedQuestionResult => {
  let deletedLocation: QuestionLocation | null = null

  const nextSkills = skills.map((skill, skillIndex) => {
    const nextQuestions: PanelQuestion[] = []
    let didDeleteInSkill = false

    for (const question of skill.questions) {
      const shouldDelete =
        !deletedLocation &&
        question.generated === true &&
        !question.answered &&
        predicate(question, skill)

      if (shouldDelete) {
        deletedLocation = {
          skillIndex,
          skillId: skill.id,
          questionId: question.id,
        }
        didDeleteInSkill = true
        continue
      }

      nextQuestions.push(question)
    }

    return didDeleteInSkill ? { ...skill, questions: nextQuestions } : skill
  })

  return {
    skills: deletedLocation ? nextSkills : skills,
    deletedLocation,
  }
}

const deleteGeneratedQuestionById = (skills: PanelSkill[], questionId: string) =>
  deleteGeneratedQuestion(skills, (question) => question.id === questionId)

const deleteFirstGeneratedQuestion = (skills: PanelSkill[]) =>
  deleteGeneratedQuestion(skills, () => true)

const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="var(--qp-green)" strokeWidth="1.5" />
    <path
      d="M5 8l2 2.5 4-5"
      stroke="var(--qp-green)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconEmpty = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="var(--qp-border)" strokeWidth="1.5" />
  </svg>
)

const IconChevron = ({ open, size = 12 }: { open: boolean; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 12 12"
    fill="none"
    style={{
      transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
      transition: 'transform 0.2s ease',
      flexShrink: 0,
    }}
  >
    <path
      d="M3 4.5l3 3 3-3"
      stroke="var(--qp-text-muted)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconSparkle = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path
      d="M7 1v4.5M7 8.5V13M1 7h4.5M8.5 7H13M3 3l2.5 2.5M8.5 8.5L11 11M11 3L8.5 5.5M5.5 8.5L3 11"
      stroke="var(--qp-accent)"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
)

const IconPlus = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M6 2v8M2 6h8" stroke="var(--qp-accent)" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const IconReset = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path
      d="M13 8a5 5 0 1 1-1.46-3.54"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 2.5H13v2.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconClose = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path
      d="M4 4l8 8M12 4l-8 8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ProgressRing = ({ ratio, size = 16 }: { ratio: number; size?: number }) => {
  const r = 6
  const circ = 2 * Math.PI * r
  const filled = circ * ratio

  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r={r} stroke="var(--qp-border)" strokeWidth="1.5" />
      {ratio > 0 && (
        <circle
          cx="8"
          cy="8"
          r={r}
          stroke="var(--qp-green)"
          strokeWidth="1.5"
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 8 8)"
          style={{ transition: 'stroke-dasharray 0.4s ease' }}
        />
      )}
    </svg>
  )
}

const QuestionItem = ({
  question,
  isLast,
  isCurrent,
  onSelect,
  onDelete,
}: {
  question: PanelQuestion
  isLast: boolean
  isCurrent: boolean
  onSelect: () => void
  onDelete: () => void
}) => {
  const isActive = isCurrent && !question.answered
  const canDelete = question.generated === true && !question.answered

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return
        }

        event.preventDefault()
        onSelect()
      }}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 0',
        borderBottom: isLast ? 'none' : '0.5px solid var(--qp-divider)',
        cursor: 'pointer',
        outline: 'none',
      }}
    >
      <div style={{ marginTop: 1, flexShrink: 0 }}>
        {question.answered ? <IconCheck size={16} /> : <IconEmpty size={16} />}
      </div>
      <span
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          fontSize: 15,
          lineHeight: '1.45',
          color: isActive
            ? 'var(--qp-accent)'
            : question.answered
              ? 'var(--qp-text-done)'
              : 'var(--qp-text-primary)',
          textDecoration: question.answered ? 'line-through' : 'none',
          transition: 'color 0.2s, text-decoration 0.2s',
          fontWeight: isActive ? 500 : 400,
        }}
      >
        {question.text}
      </span>
      {canDelete && (
        <button
          type="button"
          className="qp-question-delete"
          onClick={(event) => {
            event.stopPropagation()
            onDelete()
          }}
          onKeyDown={(event) => {
            event.stopPropagation()
          }}
          title="Удалить сгенерированный вопрос"
          aria-label="Удалить сгенерированный вопрос"
        >
          <IconClose size={14} />
        </button>
      )}
    </div>
  )
}

const NewQuestionHighlight = ({ text }: { text: string }) => (
  <div
    className="qp-new-question"
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '10px 0',
      borderBottom: 'none',
    }}
  >
    <div style={{ marginTop: 1, flexShrink: 0 }}>
      <IconSparkle size={16} />
    </div>
    <div>
      <span
        style={{
          display: 'inline-block',
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--qp-accent)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 2,
        }}
      >
        Следующий вопрос
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 15,
          lineHeight: '1.45',
          color: 'var(--qp-text-primary)',
        }}
      >
        {text}
      </span>
    </div>
  </div>
)

const SkillSection = ({
  skill,
  index,
  isOpen,
  isFocused,
  isGenerating,
  currentQuestionId,
  onToggle,
  onGenerate,
  onSelectQuestion,
  onDeleteQuestion,
  newQuestion,
  sectionRef,
}: {
  skill: PanelSkill
  index: number
  isOpen: boolean
  isFocused: boolean
  isGenerating: boolean
  currentQuestionId?: string
  onToggle: () => void
  onGenerate: (skillId: string) => void
  onSelectQuestion: (questionId: string) => void
  onDeleteQuestion: (questionId: string) => void
  newQuestion?: string | null
  sectionRef?: (element: HTMLDivElement | null) => void
}) => {
  const answeredCount = skill.questions.filter((question) => question.answered).length
  const totalCount = skill.questions.length
  const ratio = totalCount > 0 ? answeredCount / totalCount : 0
  const allDone = totalCount > 0 && answeredCount === totalCount
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight)
    }
  }, [isOpen, skill.questions.length, newQuestion])

  return (
    <div
      ref={sectionRef}
      style={{
        background: 'var(--qp-card-bg)',
        borderRadius: 10,
        overflow: 'hidden',
        transition: 'background 0.2s, box-shadow 0.2s',
        boxShadow: isFocused ? 'inset 0 0 0 1.5px var(--qp-accent)' : 'none',
      }}
    >
      <button
        onClick={onToggle}
        type="button"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '12px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconChevron open={isOpen} />
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: isFocused ? 'var(--qp-accent)' : 'var(--qp-text-muted)',
              opacity: 0.6,
              width: 14,
              textAlign: 'center',
              flexShrink: 0,
              fontVariantNumeric: 'tabular-nums',
              transition: 'color 0.2s',
            }}
          >
            {index + 1}
          </span>
          <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--qp-text-primary)' }}>
            {skill.name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: allDone ? 'var(--qp-green)' : 'var(--qp-text-muted)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {answeredCount}/{totalCount}
          </span>
          {allDone ? <IconCheck size={16} /> : <ProgressRing ratio={ratio} size={16} />}
        </div>
      </button>

      <div
        style={{
          maxHeight: isOpen ? height + 20 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div ref={contentRef} style={{ padding: '0 14px 10px' }}>
          {skill.questions.map((question, questionIndex) => (
            <QuestionItem
              key={question.id}
              question={question}
              isCurrent={currentQuestionId === question.id}
              isLast={questionIndex === skill.questions.length - 1 && !newQuestion}
              onSelect={() => onSelectQuestion(question.id)}
              onDelete={() => onDeleteQuestion(question.id)}
            />
          ))}

          {newQuestion && <NewQuestionHighlight text={newQuestion} />}

          <button
            onClick={(event) => {
              event.stopPropagation()
              onGenerate(skill.id)
            }}
            type="button"
            className="qp-gen-btn"
            disabled={isGenerating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 6,
              padding: '6px 10px',
              background: 'var(--qp-gen-bg)',
              border: 'none',
              borderRadius: 8,
              cursor: isGenerating ? 'wait' : 'pointer',
              opacity: isGenerating ? 0.7 : 1,
              transition: 'background 0.15s, opacity 0.2s',
            }}
          >
            {isGenerating ? <span className="qp-spinner" /> : <IconPlus size={12} />}
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--qp-accent)' }}>
              {isGenerating ? 'Генерируется...' : 'Сгенерировать'}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default function TextAgentQuestionsPanel({ isOpen, onClose }: TextAgentQuestionsPanelProps) {
  const room = useMaybeRoomContext()
  const prompt = useAppSelector(selectPrompt)
  const questionSections = useAppSelector(selectQuestionSections)

  const initialSkills = useMemo(
    () =>
      questionSections.length > 0
        ? buildSectionsFromCatalog(questionSections)
        : buildSectionsFromPrompt(prompt),
    [prompt, questionSections],
  )

  const [skills, setSkills] = useState<PanelSkill[]>(initialSkills)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    initialSkills.forEach((skill) => {
      const allDone = skill.questions.every((question) => question.answered)
      initial[skill.id] = !allDone
    })
    return initial
  })
  const [newQuestions, setNewQuestions] = useState<Record<string, string | null>>({})
  const [generatingSkillIds, setGeneratingSkillIds] = useState<Record<string, boolean>>({})
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [activeQuestionLocation, setActiveQuestionLocation] = useState<QuestionLocation | null>(
    () => getFirstUnansweredQuestionLocation(initialSkills),
  )
  const skillRefs = useRef<Array<HTMLDivElement | null>>([])
  const pendingGenerateRequestsRef = useRef<Record<string, string>>({})
  const lastPublishedCatalogRef = useRef<string | null>(null)
  const questionSessionIdRef = useRef<string | undefined>(undefined)
  const [catalogReady, setCatalogReady] = useState(false)

  const resetQuestionsState = useCallback(() => {
    setSkills(initialSkills)
    setOpenSections(() => {
      const initial: Record<string, boolean> = {}
      initialSkills.forEach((skill) => {
        const allDone = skill.questions.every((question) => question.answered)
        initial[skill.id] = !allDone
      })
      return initial
    })
    setNewQuestions({})
    setGeneratingSkillIds({})
    pendingGenerateRequestsRef.current = {}
    setActiveQuestionLocation(getFirstUnansweredQuestionLocation(initialSkills))
    setFocusedIndex(-1)
    setIsCollapsed(false)
  }, [initialSkills])

  useEffect(() => {
    lastPublishedCatalogRef.current = null
    resetQuestionsState()
  }, [initialSkills, resetQuestionsState])

  const publishQuestionCatalog = useCallback(
    async (
      catalogSkills: PanelSkill[] = skills,
      force = false,
      sessionId = questionSessionIdRef.current,
    ) => {
      if (!room) {
        return
      }

      const catalogMessage = buildQuestionCatalogMessage(catalogSkills, sessionId)

      const serializedCatalog = JSON.stringify(catalogMessage)
      if (!force && lastPublishedCatalogRef.current === serializedCatalog) {
        return
      }

      lastPublishedCatalogRef.current = serializedCatalog

      try {
        await handlePublishData(room, serializedCatalog, {
          reliable: true,
          topic: 'lk-question-control-topic',
        })
      } catch (error) {
        lastPublishedCatalogRef.current = null
        throw error
      }
    },
    [room, skills],
  )

  useEffect(() => {
    if (!room) {
      return
    }

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      try {
        const decodedMessage = new TextDecoder('utf-8').decode(payload)
        const catalogRequest = parseQuestionCatalogRequest(
          participant?.identity,
          topic,
          decodedMessage,
        )
        if (catalogRequest) {
          const requestedSessionId = catalogRequest.sessionId
          const isNewSession = Boolean(
            requestedSessionId && requestedSessionId !== questionSessionIdRef.current,
          )
          const catalogSkills = isNewSession ? initialSkills : skills

          if (requestedSessionId) {
            questionSessionIdRef.current = requestedSessionId
          }
          if (isNewSession) {
            lastPublishedCatalogRef.current = null
            resetQuestionsState()
          }

          setCatalogReady(true)
          void publishQuestionCatalog(
            catalogSkills,
            true,
            requestedSessionId || questionSessionIdRef.current,
          ).catch((error) => {
            console.error('Failed to resync question catalog:', error)
          })
          return
        }

        const catalogState = parseQuestionCatalogState(
          participant?.identity,
          topic,
          decodedMessage,
        )
        if (catalogState) {
          const stateSessionId = catalogState.sessionId?.trim() || undefined
          const isNewSession = Boolean(
            stateSessionId && stateSessionId !== questionSessionIdRef.current,
          )
          if (stateSessionId) {
            questionSessionIdRef.current = stateSessionId
          }

          const nextSkills = applyQuestionCatalogState(skills, catalogState.questions)
          if (nextSkills.length === 0 && initialSkills.length > 0) {
            return
          }

          lastPublishedCatalogRef.current = JSON.stringify(
            buildQuestionCatalogMessage(nextSkills, questionSessionIdRef.current),
          )
          setCatalogReady(true)

          if (isNewSession) {
            setNewQuestions({})
            setGeneratingSkillIds({})
            pendingGenerateRequestsRef.current = {}
            setIsCollapsed(false)
          }

          const nextLocation = getFirstUnansweredQuestionLocation(nextSkills)
          setSkills(nextSkills)
          setActiveQuestionLocation(nextLocation)
          setFocusedIndex(nextLocation?.skillIndex ?? -1)
          if (nextLocation) {
            setOpenSections((prev) => ({
              ...prev,
              [nextLocation.skillId]: true,
            }))
          }
          return
        }

        const askedQuestionId = resolveAskedQuestionId(
          topic,
          decodedMessage,
          questionSessionIdRef.current,
        )
        if (askedQuestionId) {
          const askedLocation = findQuestionLocation(
            skills,
            (question) => !question.answered && question.id === askedQuestionId,
          )

          if (!askedLocation) {
            return
          }

          const nextSkills = markQuestionCompleted(skills, askedQuestionId)
          const nextLocation = getNextUnansweredQuestionLocation(
            nextSkills,
            askedLocation,
            askedQuestionId,
          )

          setSkills(nextSkills)
          setActiveQuestionLocation(nextLocation)
          setFocusedIndex(nextLocation?.skillIndex ?? askedLocation.skillIndex)
          setOpenSections((prev) => ({
            ...prev,
            [askedLocation.skillId]: true,
            ...(nextLocation ? { [nextLocation.skillId]: true } : {}),
          }))

          return
        }

        if (!topic?.includes('lk-question-generated-topic')) {
          return
        }

        const parsedMessage = JSON.parse(decodedMessage) as GeneratedQuestionMessage
        const skillId = pendingGenerateRequestsRef.current[parsedMessage.requestId]

        if (!skillId) {
          return
        }

        delete pendingGenerateRequestsRef.current[parsedMessage.requestId]

        setGeneratingSkillIds((prev) => ({
          ...prev,
          [skillId]: false,
        }))

        if (parsedMessage.error) {
          console.error(`Generated question error for ${skillId}:`, parsedMessage.error)
        }

        if (!parsedMessage.question?.trim()) {
          return
        }

        const generatedQuestion = parsedMessage.question.trim()

        setSkills((prevSkills) =>
          prevSkills.map((skill) => {
            if (skill.id !== skillId) {
              return skill
            }

            const alreadyExists = skill.questions.some((question) =>
              areQuestionsMatching(question.text, generatedQuestion),
            )

            if (alreadyExists) {
              return skill
            }

            return {
              ...skill,
              questions: [
                ...skill.questions,
                {
                  id: `${skillId}-generated-${Date.now()}`,
                  text: generatedQuestion,
                  asked: false,
                  answered: false,
                  generated: true,
                },
              ],
            }
          }),
        )

        setNewQuestions((prev) => ({
          ...prev,
          [skillId]: null,
        }))

        setOpenSections((prev) => ({
          ...prev,
          [skillId]: true,
        }))

        const nextIndex = skills.findIndex((skill) => skill.id === skillId)
        if (nextIndex >= 0) {
          setFocusedIndex(nextIndex)
        }
      } catch (error) {
        console.error('Failed to parse generated question payload:', error)
      }
    }

    room.on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [initialSkills, publishQuestionCatalog, resetQuestionsState, room, skills])

  useEffect(() => {
    if (!room) {
      return
    }

    void handlePublishData(
      room,
      JSON.stringify({ action: 'request_question_catalog_state' }),
      {
        reliable: true,
        topic: 'lk-question-control-topic',
      },
    ).catch((error) => {
      console.error('Failed to request question catalog state:', error)
    })
  }, [room])

  useEffect(() => {
    if (!catalogReady) {
      return
    }

    void publishQuestionCatalog(skills).catch((error) => {
      console.error('Failed to sync question catalog:', error)
    })
  }, [catalogReady, publishQuestionCatalog, skills])

  const totalAnswered = useMemo(
    () =>
      skills.reduce(
        (sum, skill) => sum + skill.questions.filter((question) => question.answered).length,
        0,
      ),
    [skills],
  )

  const totalQuestions = useMemo(
    () => skills.reduce((sum, skill) => sum + skill.questions.length, 0),
    [skills],
  )

  const overallRatio = totalQuestions > 0 ? totalAnswered / totalQuestions : 0

  const toggleSection = useCallback((id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleGenerate = useCallback(
    async (skillId: string) => {
      if (!room) {
        return
      }

      const skill = skills.find((item) => item.id === skillId)
      if (!skill || generatingSkillIds[skillId]) {
        return
      }

      const requestId = `${skillId}-${Date.now()}`
      pendingGenerateRequestsRef.current[requestId] = skillId

      setOpenSections((prev) => ({ ...prev, [skillId]: true }))
      setFocusedIndex(skills.findIndex((item) => item.id === skillId))
      setGeneratingSkillIds((prev) => ({
        ...prev,
        [skillId]: true,
      }))

      try {
        await handlePublishData(
          room,
          JSON.stringify({
            action: 'generate_question',
            requestId,
            skillId,
            skillName: skill.name,
            existingQuestions: skill.questions.map((question) => question.text),
          }),
          {
            reliable: true,
            topic: 'lk-question-control-topic',
          },
        )
      } catch (error) {
        delete pendingGenerateRequestsRef.current[requestId]
        setGeneratingSkillIds((prev) => ({
          ...prev,
          [skillId]: false,
        }))
        console.error('Failed to request generated question:', error)
      }
    },
    [room, skills, generatingSkillIds],
  )

  const applyDeleteResult = useCallback(
    (result: DeleteGeneratedQuestionResult) => {
      const { deletedLocation } = result

      if (!deletedLocation) {
        return
      }

      const activeLocationExists = activeQuestionLocation
        ? Boolean(
            findQuestionLocation(
              result.skills,
              (question) => question.id === activeQuestionLocation.questionId,
            ),
          )
        : false
      const nextLocation = activeLocationExists
        ? activeQuestionLocation
        : getFirstUnansweredQuestionLocation(result.skills)

      setSkills(result.skills)
      setActiveQuestionLocation(nextLocation)
      setFocusedIndex(nextLocation?.skillIndex ?? deletedLocation.skillIndex)
      setOpenSections((prev) => ({
        ...prev,
        [deletedLocation.skillId]: true,
        ...(nextLocation ? { [nextLocation.skillId]: true } : {}),
      }))
      setNewQuestions((prev) => ({
        ...prev,
        [deletedLocation.skillId]: null,
      }))
    },
    [activeQuestionLocation],
  )

  const handleDeleteGeneratedQuestion = useCallback(
    (questionId: string) => {
      applyDeleteResult(deleteGeneratedQuestionById(skills, questionId))
    },
    [applyDeleteResult, skills],
  )

  const handleDeleteFirstGeneratedQuestion = useCallback(() => {
    applyDeleteResult(deleteFirstGeneratedQuestion(skills))
  }, [applyDeleteResult, skills])

  useEffect(() => {
    const isInputFocused = () => {
      const element = document.activeElement as HTMLElement | null
      if (!element) return false
      const tag = element.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || element.isContentEditable
    }

    const getEffectiveFocusIndex = () => {
      if (focusedIndex >= 0 && focusedIndex < skills.length) return focusedIndex
      const idx = skills.findIndex((skill) =>
        skill.questions.some((question) => !question.answered),
      )
      return idx >= 0 ? idx : 0
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isInputFocused() || isCollapsed || !isOpen || skills.length === 0) return

      const { key } = event

      if (event.shiftKey && key >= '1' && key <= '9') {
        const idx = Number(key) - 1
        if (idx < skills.length) {
          event.preventDefault()
          setFocusedIndex(idx)
          handleGenerate(skills[idx].id)
        }
        return
      }

      if (key === 'ArrowDown' || key === 'j') {
        event.preventDefault()
        setFocusedIndex((prev) => {
          const next = prev < skills.length - 1 ? prev + 1 : 0
          skillRefs.current[next]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          return next
        })
        return
      }

      if (key === 'ArrowUp' || key === 'k') {
        event.preventDefault()
        setFocusedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : skills.length - 1
          skillRefs.current[next]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          return next
        })
        return
      }

      if (key === 'Enter' || key === 'ArrowRight') {
        event.preventDefault()
        const idx = getEffectiveFocusIndex()
        setFocusedIndex(idx)
        toggleSection(skills[idx].id)
        return
      }

      if (key === 'ArrowLeft') {
        event.preventDefault()
        const idx = getEffectiveFocusIndex()
        setFocusedIndex(idx)
        setOpenSections((prev) => ({ ...prev, [skills[idx].id]: false }))
        return
      }

      if (key === 'g' || key === 'G' || key === 'п' || key === 'П') {
        event.preventDefault()
        const idx = getEffectiveFocusIndex()
        setFocusedIndex(idx)
        handleGenerate(skills[idx].id)
        return
      }

      if (
        key === 'd' ||
        key === 'D' ||
        key === 'в' ||
        key === 'В' ||
        event.code === 'KeyD'
      ) {
        event.preventDefault()
        handleDeleteFirstGeneratedQuestion()
        return
      }

      if (key === 'r' || key === 'R' || key === 'к' || key === 'К') {
        event.preventDefault()
        resetQuestionsState()
        return
      }

      if (key === 'Escape') {
        event.preventDefault()
        if (isCollapsed) {
          onClose()
        } else {
          setIsCollapsed(true)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    skills,
    focusedIndex,
    isCollapsed,
    handleGenerate,
    handleDeleteFirstGeneratedQuestion,
    toggleSection,
    isOpen,
    onClose,
    resetQuestionsState,
  ])

  if (!isOpen) {
    return null
  }

  return (
    <aside
      className="lk-participant-list lk-text-agent-questions-panel"
      style={{ display: 'flex', minHeight: 0, height: '100%', overflow: 'hidden' }}
    >
      <div className="lk-participant-list-container qp-root">
        <style>{`
          .qp-root {
            --qp-bg: #141416;
            --qp-card-bg: #1e1e22;
            --qp-card-hover: #26262b;
            --qp-border: #333338;
            --qp-divider: #2a2a2f;
            --qp-text-primary: #e8e8ec;
            --qp-text-muted: #808088;
            --qp-text-done: #5c5c64;
            --qp-green: #34d399;
            --qp-accent: #a78bfa;
            --qp-gen-bg: rgba(167, 139, 250, 0.08);
            --qp-gen-hover: rgba(167, 139, 250, 0.15);
            --qp-progress-bg: #2a2a2f;

            width: 100%;
            height: 100%;
            max-height: 100%;
            min-height: 0;
            background: var(--qp-bg);
            display: flex;
            flex-direction: column;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
            color: var(--qp-text-primary);
            position: relative;
            overflow: hidden;
            border-left: 0.5px solid var(--qp-border);
          }

          .qp-mobile-close-btn {
            display: none;
          }

          @media (max-width: 600px) {
            .qp-root {
              width: 100%;
              border-left: none;
            }

            .qp-header {
              gap: 8px;
              padding: 12px 14px 10px;
            }

            .qp-title {
              flex: 1 1 auto;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              font-size: 14px;
            }

            .qp-counter {
              display: none;
            }

            .qp-header-right {
              gap: 6px;
              flex-shrink: 0;
            }

            .qp-mobile-close-btn {
              display: flex;
            }

            .qp-collapse-btn {
              display: none;
            }
          }

          .qp-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px 16px 12px;
            flex-shrink: 0;
          }

          .qp-title {
            flex: 1 1 auto;
            min-width: 0;
            font-size: 15px;
            font-weight: 600;
            color: var(--qp-text-primary);
            margin: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .qp-header-right {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
          }

          .qp-counter {
            font-size: 12px;
            color: var(--qp-text-muted);
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }

          .qp-close-btn {
            background: none;
            border: none;
            color: var(--qp-text-muted);
            cursor: pointer;
            padding: 4px;
            line-height: 1;
            font-size: 20px;
            border-radius: 6px;
            transition: background 0.15s, color 0.15s;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
          }

          .qp-close-btn:hover {
            background: var(--qp-card-bg);
            color: var(--qp-text-primary);
          }

          .qp-header-btn {
            background: none;
            border: none;
            color: var(--qp-text-muted);
            cursor: pointer;
            padding: 4px;
            line-height: 1;
            border-radius: 6px;
            transition: background 0.15s, color 0.15s;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
          }

          .qp-header-btn:hover {
            background: var(--qp-card-bg);
            color: var(--qp-text-primary);
          }

          .qp-progress-bar-container {
            padding: 0 16px 14px;
            flex-shrink: 0;
          }

          .qp-progress-track {
            display: flex;
            gap: 3px;
            height: 4px;
          }

          .qp-progress-segment {
            border-radius: 2px;
            transition: background 0.4s ease, flex 0.3s ease;
          }

          .qp-body {
            min-height: 100%;
            padding: 0 12px 16px;
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .qp-scrollable {
            overscroll-behavior: contain;
          }

          .qp-scrollable::-webkit-scrollbar {
            width: 6px;
          }

          .qp-scrollable::-webkit-scrollbar-track {
            background: transparent;
          }

          .qp-scrollable::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.15);
            border-radius: 3px;
          }

          .qp-gen-btn:hover:not(:disabled) {
            background: var(--qp-gen-hover) !important;
          }

          .qp-question-delete {
            width: 22px;
            height: 22px;
            padding: 0;
            border: none;
            border-radius: 6px;
            background: transparent;
            color: var(--qp-text-muted);
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            margin-top: -2px;
            transition: background 0.15s, color 0.15s;
          }

          .qp-question-delete:hover {
            background: var(--qp-card-hover);
            color: var(--qp-text-primary);
          }

          .qp-spinner {
            width: 14px;
            height: 14px;
            border: 2px solid var(--qp-border);
            border-top-color: var(--qp-accent);
            border-radius: 50%;
            animation: qp-spin 0.7s linear infinite;
          }

          @keyframes qp-spin {
            to { transform: rotate(360deg); }
          }

          .qp-new-question {
            animation: qp-fade-in 0.3s ease;
          }

          @keyframes qp-fade-in {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .qp-collapsed-strip {
            position: absolute;
            top: 0;
            right: 0;
            width: 48px;
            height: 100%;
            background: var(--qp-bg);
            border-left: 0.5px solid var(--qp-border);
            display: flex;
            flex-direction: column;
            align-items: center;
            padding-top: 14px;
            gap: 12px;
            z-index: 2;
          }

          .qp-collapsed-strip button {
            background: none;
            border: none;
            cursor: pointer;
            color: var(--qp-text-muted);
            padding: 6px;
            border-radius: 8px;
            transition: background 0.15s;
          }

          .qp-collapsed-strip button:hover {
            background: var(--qp-card-bg);
          }

          .qp-collapsed-counter {
            writing-mode: vertical-rl;
            text-orientation: mixed;
            font-size: 11px;
            color: var(--qp-text-muted);
            font-variant-numeric: tabular-nums;
            margin-top: 4px;
          }

          .qp-collapsed-progress {
            width: 4px;
            flex: 1;
            background: var(--qp-progress-bg);
            border-radius: 2px;
            margin: 0 0 14px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
          }

          .qp-collapsed-fill {
            background: var(--qp-green);
            border-radius: 2px;
            transition: height 0.4s ease;
          }

          .qp-hotkey-bar {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            align-items: center;
            gap: 6px 10px;
            padding: 8px 12px 12px;
            flex-shrink: 0;
            animation: qp-fade-in 0.3s ease;
          }

          .qp-hotkey-hint {
            font-size: 11px;
            color: var(--qp-text-muted);
            display: flex;
            align-items: center;
            gap: 4px;
            flex: 0 0 auto;
            white-space: nowrap;
          }

          .qp-hotkey-hint kbd {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 20px;
            height: 18px;
            padding: 0 4px;
            background: var(--qp-card-bg);
            border: 0.5px solid var(--qp-border);
            border-radius: 4px;
            font-family: inherit;
            font-size: 10px;
            font-weight: 500;
            color: var(--qp-text-primary);
            line-height: 1;
            flex: 0 0 auto;
          }
        `}</style>

        {isCollapsed ? (
          <div className="qp-collapsed-strip">
            <button onClick={() => setIsCollapsed(false)} title="Развернуть панель" type="button">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M11 4l-5 5 5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <span className="qp-collapsed-counter">
              {totalAnswered}/{totalQuestions}
            </span>
            <div className="qp-collapsed-progress">
              <div
                className="qp-collapsed-fill"
                style={{ height: `${Math.round(overallRatio * 100)}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="qp-header">
              <h2 className="qp-title">Вопросы</h2>
              <div className="qp-header-right">
                <span className="qp-counter">
                  {totalAnswered} из {totalQuestions} задано
                </span>
                <button
                  className="qp-header-btn"
                  onClick={resetQuestionsState}
                  title="Сбросить вопросы"
                  type="button"
                >
                  <IconReset size={16} />
                </button>
                <button
                  className="qp-header-btn qp-mobile-close-btn"
                  onClick={onClose}
                  title="Закрыть вопросы"
                  type="button"
                >
                  <IconClose size={16} />
                </button>
                <button
                  className="qp-close-btn qp-collapse-btn"
                  onClick={() => setIsCollapsed(true)}
                  title="Свернуть"
                  type="button"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path
                      d="M7 4l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="qp-progress-bar-container">
              <div className="qp-progress-track">
                {skills.map((skill) => {
                  const answered = skill.questions.filter((question) => question.answered).length
                  const total = skill.questions.length
                  return (
                    <div
                      key={skill.id}
                      className="qp-progress-segment"
                      style={{
                        flex: total || 1,
                        background:
                          total > 0 && answered === total
                            ? 'var(--qp-green)'
                            : answered > 0 && total > 0
                              ? `linear-gradient(90deg, var(--qp-green) ${Math.round(
                                  (answered / total) * 100,
                                )}%, var(--qp-progress-bg) ${Math.round((answered / total) * 100)}%)`
                              : 'var(--qp-progress-bg)',
                      }}
                      title={`${skill.name}: ${answered}/${total}`}
                    />
                  )
                })}
              </div>
            </div>

            <div className="lk-participant-list-scrollable qp-scrollable">
              <div className="qp-body">
                {skills.map((skill, index) => (
                  <SkillSection
                    key={skill.id}
                    skill={skill}
                    index={index}
                    isOpen={!!openSections[skill.id]}
                    isFocused={focusedIndex === index}
                    isGenerating={!!generatingSkillIds[skill.id]}
                    currentQuestionId={
                      activeQuestionLocation?.skillId === skill.id
                        ? activeQuestionLocation.questionId
                        : undefined
                    }
                    onToggle={() => {
                      setFocusedIndex(index)
                      toggleSection(skill.id)
                    }}
                    onGenerate={handleGenerate}
                    onSelectQuestion={(questionId) => {
                      const selectedQuestion = skill.questions.find(
                        (question) => question.id === questionId,
                      )

                      if (!selectedQuestion) {
                        return
                      }

                      const currentLocation = {
                        skillIndex: index,
                        skillId: skill.id,
                        questionId,
                      }
                      const nextSkills = toggleQuestionCompleted(skills, questionId)
                      const nextLocation = getNextUnansweredQuestionLocation(
                        nextSkills,
                        currentLocation,
                        questionId,
                      )

                      setSkills(nextSkills)
                      setActiveQuestionLocation(
                        selectedQuestion.answered ? currentLocation : nextLocation,
                      )
                      setFocusedIndex(
                        selectedQuestion.answered
                          ? currentLocation.skillIndex
                          : (nextLocation?.skillIndex ?? index),
                      )
                      setOpenSections((prev) => ({
                        ...prev,
                        [skill.id]: true,
                        ...(!selectedQuestion.answered && nextLocation
                          ? { [nextLocation.skillId]: true }
                          : {}),
                      }))
                    }}
                    onDeleteQuestion={handleDeleteGeneratedQuestion}
                    newQuestion={newQuestions[skill.id]}
                    sectionRef={(element) => {
                      skillRefs.current[index] = element
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="qp-hotkey-bar">
              <span className="qp-hotkey-hint">
                <kbd>↑↓</kbd> навигация
              </span>
              <span className="qp-hotkey-hint">
                <kbd>G</kbd> новый вопрос
              </span>
              <span className="qp-hotkey-hint">
                <kbd>D</kbd> удалить
              </span>
              <span className="qp-hotkey-hint">
                <kbd>R</kbd> сброс
              </span>
              <span className="qp-hotkey-hint">
                <kbd>Enter</kbd> раскрыть
              </span>
            </div>
          </>
        )}
      </div>
    </aside>
  )
}
